import { omit } from 'lodash';
import React from 'react';

import { useLogPosthogEvent } from '~src/commons/services/posthog-service';

import { Category, SearchAddress, StateResponse } from '../../../api/api';
import {
  CategoryLostModal,
  ChoiceUnavailableForAddressModal,
  LocationLostModal,
} from '../../category/category-lost-modal';
import { useCategoryState } from '../../category/category-state';
import { checkCategoryLost, checkChoiceLost } from '../../category/check-category-lost';
import { constructUserChoice, useUserChoiceMutation } from '../../category/user-choice';
import { GeneralError } from '../../commons/components/general-error';
import { FullscreenMode } from '../../fullscreen-internals/fullscreen-mode';
import { useGlobalState, useGlobalStateUpdate, useSessionState } from '../../global-state';
import { AddressFormFullscreen } from './address-form';
import { AddressMutationError, useAddressMutation } from './address-mutation';

interface AddressProviderChildProps {
  onOpen: () => void;
  onUpdate: (values: SearchAddress) => void;
}

interface AddressProviderProps {
  children: (props: AddressProviderChildProps) => React.ReactNode;
  onReset?: () => void;
}

export const AddressProvider: React.FC<AddressProviderProps> = ({
  children,
  onReset = () => undefined,
}) => {
  const logPosthogEvent = useLogPosthogEvent();
  const updateGlobalState = useGlobalStateUpdate();
  const oldState = useGlobalState();

  const [addressOpen, setAddressOpen] = React.useState(false);
  const [categoryLost, setCategoryLost] = React.useState(false);
  const [locationLost, setLocationLost] = React.useState(false);

  const addressMutation = useAddressMutation();

  const callUpdateState = (newState: StateResponse) => {
    if (checkCategoryLost(newState, oldState)) {
      setCategoryLost(true);
    } else if (checkChoiceLost(newState, oldState)) {
      setLocationLost(true);
    }

    updateGlobalState(newState);
  };

  const callResetState = () => {
    onReset();
  };

  const childProps = {
    onOpen: () => setAddressOpen(true),
    onUpdate: (values: SearchAddress) => {
      return addressMutation.mutate(values, { onSuccess: callUpdateState });
    },
  };

  return (
    <>
      {children(childProps)}
      {addressOpen && (
        <FullscreenMode>
          <AddressFormFullscreen
            onSubmit={(values: SearchAddress) => {
              addressMutation.mutate(values, { onSuccess: callUpdateState });
              logPosthogEvent('Search Address Changed', omit(values, ['address_lines']));
              setAddressOpen(false);
            }}
            onClose={() => {
              logPosthogEvent('Address Change Canceled');
              setAddressOpen(false);
              callResetState();
            }}
          />
        </FullscreenMode>
      )}
      <AddressMutationError
        error={addressMutation.error}
        onClose={() => {
          addressMutation.reset();
          callResetState();
        }}
        onSubmit={() => {
          addressMutation.reset();
          setAddressOpen(true);
        }}
      />
      {categoryLost && (
        <ChoiceUnavailableForAddressModal
          onClose={() => {
            setCategoryLost(false);
            callResetState();
          }}
          onRepeat={() => {
            setCategoryLost(false);
            setAddressOpen(true);
          }}
        />
      )}
      {oldState.sessions[0].flags.show_location_unavailable_modal_message && locationLost && (
        <LocationLostModal
          onClose={() => {
            setLocationLost(false);
          }}
        />
      )}
    </>
  );
};

interface ChildProps {
  onOpen: () => void;
}

interface CategoryAddressProviderProps {
  children: (props: ChildProps) => React.ReactNode;
  onUpdateAddress?: () => void;
}

/*
 * If we select a category that requires an address we perform the
 * following workflow:
 *
 * 1. Save the address to backend and get a new list of categories.
 * 2. Check whether the category is still available (still has options)
 * 3. Select a new option from this category because the old option might
 *    not be valid (options change after we add the address).
 */
export const CategoryAddressProvider: React.FC<CategoryAddressProviderProps> = ({
  children,
  onUpdateAddress = () => undefined,
}) => {
  const updateGlobalState = useGlobalStateUpdate();
  const session = useSessionState();
  const category = useCategoryState();

  const logPosthogEvent = useLogPosthogEvent();

  const [upcomingState, setUpcomingState] = React.useState<StateResponse | undefined>(undefined);

  const [addressOpen, setAddressOpen] = React.useState<boolean>(false);
  const [categoryLost, setCategoryLost] = React.useState<boolean>(false);

  const addressMutation = useAddressMutation();
  const userChoiceMutation = useUserChoiceMutation();

  const callUpdateChoice = (newState: StateResponse) => {
    const newSession = newState.sessions.find(
      newSession => newSession.session_id === session.session_id
    );
    const selectedCategoryInNewState = newSession!.categories.find(
      (newCategory: Category) => newCategory.id === category.id
    );
    if (selectedCategoryInNewState) {
      updateGlobalState(newState);
      /**
       * Here we disrespect the actual choice because what was pre-address is
       * irrelevant. We just create a new choice based on the selected
       * category.
       */
      const newChoice = constructUserChoice(selectedCategoryInNewState);
      userChoiceMutation.mutate(newChoice, {
        onSuccess: (newState: StateResponse) => {
          updateGlobalState(newState);
        },
      });
      setTimeout(() => {
        // one cycle is need for query data to propagate.
        onUpdateAddress();
      });
    } else {
      setUpcomingState(newState);
      // show error modal, sync state later.
      setCategoryLost(true);
    }
  };

  const callResetChoice = () => {
    // reset to backend
    if (upcomingState) {
      updateGlobalState(upcomingState);
    }
  };

  const childProps = {
    onOpen: () => setAddressOpen(true),
  };

  return (
    <>
      {children(childProps)}
      {addressOpen && (
        <FullscreenMode>
          <AddressFormFullscreen
            onSubmit={(values: SearchAddress) => {
              addressMutation.mutate(values, {
                onSuccess: newState => {
                  callUpdateChoice(newState);
                  logPosthogEvent('Search Address Changed', omit(values, ['address_lines']));
                },
              });
              setAddressOpen(false);
            }}
            onClose={() => {
              setAddressOpen(false);
              callResetChoice();
              logPosthogEvent('Address Change Canceled');
            }}
          />
        </FullscreenMode>
      )}
      <GeneralError error={userChoiceMutation.error} onClose={userChoiceMutation.reset} />
      <AddressMutationError
        error={addressMutation.error}
        onClose={() => {
          addressMutation.reset();
          /**
           * Now the mutation returned error, but previously there might have
           * been a valid state.
           */
          callResetChoice();
        }}
        onSubmit={() => {
          addressMutation.reset();
          setAddressOpen(true);
        }}
      />
      {categoryLost && (
        <CategoryLostModal
          category={category}
          onClose={() => {
            setCategoryLost(false);
            callResetChoice();
          }}
          onRepeat={() => {
            setCategoryLost(false);
            setAddressOpen(true);
          }}
        />
      )}
    </>
  );
};
